import React, { useEffect, useMemo, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { ArrowRight, Clock, Film, Search, Tv, Users, X } from 'lucide-react';
import usePersonPhotos from '../../hooks/usePersonPhotos';
import { useCatalog } from '../../providers/CatalogProvider';
import { initials, searchPeople, searchTitles, subtitleFor } from '../../lib/catalog';
import { actorPath, explorePath } from '../../lib/routes';

const RECENT_KEY = 'cinestream:recent-searches';
const MAX_RECENT = 6;

function readRecent() {
  try {
    return JSON.parse(localStorage.getItem(RECENT_KEY) || '[]').slice(0, MAX_RECENT);
  } catch {
    return [];
  }
}

function pushRecent(term) {
  const clean = (term || '').trim();
  if (clean.length < 2) return;
  try {
    const next = [clean, ...readRecent().filter((item) => item !== clean)].slice(0, MAX_RECENT);
    localStorage.setItem(RECENT_KEY, JSON.stringify(next));
  } catch {
    // stockage indisponible : la recherche fonctionne quand même
  }
}

/** Met en évidence la portion trouvée, pour rendre le classement lisible. */
function Highlight({ text, term }) {
  const at = text.toLowerCase().indexOf(term.toLowerCase());
  if (!term || at < 0) return <>{text}</>;
  return (
    <>
      {text.slice(0, at)}
      <mark className="hit bg-transparent">{text.slice(at, at + term.length)}</mark>
      {text.slice(at + term.length)}
    </>
  );
}

/**
 * Recherche plein écran.
 *
 * Une seule saisie interroge trois choses à la fois : les titres, les acteurs
 * et les réalisateurs. C'est le point d'entrée principal du parcours par
 * acteur — taper « pitt » propose Brad Pitt avant même ses films.
 *
 * Tout se calcule en mémoire sur le catalogue déjà chargé : aucune latence
 * réseau, donc aucun besoin de temporisation à la frappe.
 */
export default function SearchOverlay({ onClose }) {
  const navigate = useNavigate();
  const catalog = useCatalog();
  const inputRef = useRef(null);
  const [term, setTerm] = useState('');
  const [recent, setRecent] = useState(readRecent);

  useEffect(() => {
    inputRef.current?.focus();
    const previous = document.body.style.overflow;
    document.body.style.overflow = 'hidden';

    const onKey = (event) => {
      if (event.key === 'Escape') onClose();
    };
    document.addEventListener('keydown', onKey);

    return () => {
      document.body.style.overflow = previous;
      document.removeEventListener('keydown', onKey);
    };
  }, [onClose]);

  const active = term.trim().length >= 2;

  const titles = useMemo(
    () => (active ? searchTitles(catalog?.titles || [], term, 12) : []),
    [catalog?.titles, term, active]
  );
  const people = useMemo(
    () => (active ? searchPeople(catalog?.people, term, 6) : []),
    [catalog?.people, term, active]
  );
  const { people: photos } = usePersonPhotos(people.map((person) => person.name));

  const leave = (action) => {
    pushRecent(term);
    setRecent(readRecent());
    onClose();
    action();
  };

  const openTitle = (title) => leave(() => catalog?.onOpenTitle(title));
  const openPerson = (name) => leave(() => navigate(actorPath(name)));
  const openAll = () => leave(() => navigate(explorePath({ q: term })));

  return (
    <div className="omni" role="dialog" aria-modal="true" aria-label="Recherche">
      <div className="omni-bar">
        <Search className="w-5 h-5 text-[var(--c-gold)] flex-none" aria-hidden="true" />
        <input
          ref={inputRef}
          className="omni-input"
          type="search"
          value={term}
          onChange={(event) => setTerm(event.target.value)}
          onKeyDown={(event) => {
            if (event.key === 'Enter' && active) openAll();
          }}
          placeholder="Un titre, un acteur, un réalisateur…"
          aria-label="Rechercher"
        />
        <button type="button" className="icon-btn" onClick={onClose} aria-label="Fermer la recherche">
          <X aria-hidden="true" />
        </button>
      </div>

      <div className="omni-scroll">
        {/* ── Repos : historique et suggestions ── */}
        {!active && (
          <>
            {recent.length > 0 && (
              <section className="omni-group">
                <div className="omni-group-title">
                  <Clock className="w-3.5 h-3.5 text-[var(--t-faint)]" aria-hidden="true" />
                  <p className="eyebrow">Recherches récentes</p>
                </div>
                <div className="flex flex-wrap gap-2">
                  {recent.map((item) => (
                    <button key={item} type="button" className="chip" onClick={() => setTerm(item)}>
                      {item}
                    </button>
                  ))}
                </div>
              </section>
            )}

            <section className="omni-group">
              <div className="omni-group-title">
                <Users className="w-3.5 h-3.5 text-[var(--t-faint)]" aria-hidden="true" />
                <p className="eyebrow">Têtes d'affiche du moment</p>
              </div>
              <div className="flex flex-wrap gap-2">
                {(catalog?.people.actors || []).slice(0, 10).map((person) => (
                  <button
                    key={person.key}
                    type="button"
                    className="chip"
                    onClick={() => openPerson(person.name)}
                  >
                    {person.name}
                  </button>
                ))}
              </div>
            </section>
          </>
        )}

        {/* ── Personnes ── */}
        {people.length > 0 && (
          <section className="omni-group">
            <div className="omni-group-title">
              <Users className="w-3.5 h-3.5 text-[var(--c-gold)]" aria-hidden="true" />
              <p className="eyebrow">Acteurs & réalisateurs</p>
            </div>
            {people.map((person) => (
              <button
                key={person.key}
                type="button"
                className="omni-row"
                onClick={() => openPerson(person.name)}
              >
                {photos[person.name]?.photo ? (
                  <img
                    className="omni-thumb omni-thumb-face"
                    src={photos[person.name].photo}
                    alt=""
                    loading="lazy"
                  />
                ) : (
                  <span className="omni-thumb omni-thumb-face grid place-items-center text-[var(--t-faint)] font-semibold">
                    {initials(person.name)}
                  </span>
                )}
                <span className="min-w-0 flex-1">
                  <span className="omni-name block truncate">
                    <Highlight text={person.name} term={term.trim()} />
                  </span>
                  <span className="omni-sub block">
                    {person.count} titre{person.count > 1 ? 's' : ''} ici
                    {person.films > 0 && person.series > 0
                      ? ` · ${person.films} film${person.films > 1 ? 's' : ''}, ${person.series} série${person.series > 1 ? 's' : ''}`
                      : ''}
                  </span>
                </span>
                <ArrowRight className="w-4 h-4 text-[var(--t-faint)] flex-none" aria-hidden="true" />
              </button>
            ))}
          </section>
        )}

        {/* ── Titres ── */}
        {titles.length > 0 && (
          <section className="omni-group">
            <div className="omni-group-title">
              <Film className="w-3.5 h-3.5 text-[var(--c-gold)]" aria-hidden="true" />
              <p className="eyebrow">Films & séries</p>
            </div>
            {titles.map((title) => (
              <button
                key={title.id}
                type="button"
                className="omni-row"
                onClick={() => openTitle(title)}
              >
                {title.poster ? (
                  <img className="omni-thumb" src={title.poster} alt="" loading="lazy" />
                ) : (
                  <span className="omni-thumb grid place-items-center text-[var(--t-faint)]">
                    <Tv className="w-4 h-4" aria-hidden="true" />
                  </span>
                )}
                <span className="min-w-0 flex-1">
                  <span className="omni-name block truncate">
                    <Highlight text={title.title} term={term.trim()} />
                  </span>
                  <span className="omni-sub block truncate">
                    {title.type === 'serie' ? 'Série' : 'Film'} · {title.year} · {subtitleFor(title)}
                  </span>
                </span>
                <ArrowRight className="w-4 h-4 text-[var(--t-faint)] flex-none" aria-hidden="true" />
              </button>
            ))}

            <button type="button" className="btn btn-sm btn-outline mt-3" onClick={openAll}>
              Voir tous les résultats
              <ArrowRight aria-hidden="true" />
            </button>
          </section>
        )}

        {/* ── Rien trouvé ── */}
        {active && titles.length === 0 && people.length === 0 && (
          <div className="empty">
            <span className="empty-orb">
              <Search aria-hidden="true" />
            </span>
            <h3>Rien pour « {term} »</h3>
            <p>Vérifiez l'orthographe, ou tentez le nom d'un acteur plutôt qu'un titre.</p>
          </div>
        )}
      </div>
    </div>
  );
}
